import React, { Component } from 'react';
import { Query, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { Alert, Button, Card, Form, Input, Modal, Spin, message } from 'antd';
import { GET_CURRENT_USER } from '../api/user';
import './ReleaseSettings.css';

const FIELDS = gql`fragment ReleaseFields on ReleaseConfig { version wgtVersion downloadLink description revision }`;
const LOAD = gql`query ReleaseConfig { releaseConfig { ...ReleaseFields } } ${FIELDS}`;
const SAVE = gql`mutation UpdateReleaseConfig($data: ReleaseConfigInput!) {
  updateReleaseConfig(data: $data) { ...ReleaseFields }
} ${FIELDS}`;

export class ReleaseSettingsForm extends Component {
  state = { current: null, version: '', wgtVersion: '', downloadLink: '', description: '', loading: true, saving: false, error: '' };
  mounted = false;

  componentDidMount() { this.mounted = true; this.load(); }
  componentWillUnmount() { this.mounted = false; }

  apply = config => {
    if (this.mounted) this.setState({ current: config, version: config.version, wgtVersion: config.wgtVersion, downloadLink: config.downloadLink, description: config.description });
  };

  load = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const { data } = await this.props.client.query({ query: LOAD, fetchPolicy: 'network-only' });
      this.apply(data.releaseConfig);
    } catch (error) {
      if (this.mounted) this.setState({ error: error.message });
    } finally {
      if (this.mounted) this.setState({ loading: false });
    }
  };

  save = async data => {
    if (this.state.saving) return;
    this.setState({ saving: true, error: '' });
    try {
      const result = await this.props.client.mutate({ mutation: SAVE, variables: { data } });
      this.apply(result.data.updateReleaseConfig);
      message.success('版本配置已保存，新启动的客户端会读取新配置');
    } catch (error) {
      if (this.mounted) this.setState({ error: error.message });
      message.error('保存失败，输入已保留，请核对后重试');
    } finally {
      if (this.mounted) this.setState({ saving: false });
    }
  };

  confirmSave = event => {
    event.preventDefault();
    const { current, saving } = this.state;
    if (!current || saving) return;
    const data = { version: this.state.version.trim(), wgtVersion: this.state.wgtVersion.trim(), downloadLink: this.state.downloadLink.trim(), description: this.state.description, revision: current.revision };
    if (data.description.length > 5000) return message.error('更新说明不能超过 5000 个字符');
    const validVersion = value => /^\d+\.\d+\.\d+$/.test(value) && value.length <= 32 && value.split('.').every(part => Number.isSafeInteger(Number(part)));
    if (!validVersion(data.version) || !validVersion(data.wgtVersion)) return message.error('版本号请填写三段非负整数，例如 3.6.0');
    try {
      const url = new URL(data.downloadLink);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash || !/\.wgt$/i.test(url.pathname) || data.downloadLink.length > 2048) throw new Error();
    } catch (error) { return message.error('请填写 HTTPS 的 .wgt 文件地址，不得包含账号密码或锚点'); }
    if (['version', 'wgtVersion', 'downloadLink', 'description'].every(key => data[key] === current[key])) return message.info('配置没有变化');
    Modal.confirm({
      title: '确认保存版本配置？',
      content: <div className="release-confirm">
        <p>首页版本：{current.version || '未设置'} → {data.version}</p>
        <p>WGT 版本：{current.wgtVersion || '未设置'} → {data.wgtVersion}</p>
        <p>下载地址：{data.downloadLink}</p>
        <p style={{ whiteSpace: 'pre-wrap' }}>更新说明：{data.description || '（空）'}</p>
        <p>请确认资源包已上传且包内版本与填写版本一致。保存后客户端可能开始更新。</p>
      </div>,
      okText: '确认保存', cancelText: '返回检查',
      onOk: () => this.save(data),
    });
  };

  render() {
    const { current, version, wgtVersion, downloadLink, description, loading, saving, error } = this.state;
    const disabled = loading || saving;
    return <div className="release-settings">
      <h2>版本与热更新</h2>
      <Alert type="info" showIcon message="首页版本与 WGT 更新分别控制" description="首页版本影响客户端功能判断；WGT 版本与地址决定热更新。修改首页版本不会生成或上传资源包。" />
      <div className="release-toolbar"><Button disabled={disabled} onClick={() => {
        const dirty = current && ['version', 'wgtVersion', 'downloadLink', 'description'].some(key => this.state[key] !== current[key]);
        if (dirty) Modal.confirm({ title: '重新加载会丢弃未保存的输入，是否继续？', onOk: this.load });
        else this.load();
      }}>重新加载配置</Button></div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        <Form onSubmit={this.confirmSave}>
          <fieldset disabled={disabled || !current}>
            <Card title="首页版本">
              <Form.Item label="当前正式版本" extra={`已保存：${current ? current.version || '未设置' : '尚未读取'}`}>
                <Input aria-label="首页版本号" value={version} placeholder="例如 3.6.0" onChange={e => this.setState({ version: e.target.value })} />
              </Form.Item>
              <p>客户端版本高于此值时，现有 App 会进入开发模式。正式发布时请同步核对。</p>
            </Card>
            <Card title="WGT 热更新">
              <Form.Item label="资源包版本" extra={`已保存：${current ? current.wgtVersion || '未设置' : '尚未读取'}`}>
                <Input aria-label="WGT 版本号" value={wgtVersion} placeholder="例如 3.6.0" onChange={e => this.setState({ wgtVersion: e.target.value })} />
              </Form.Item>
              <Form.Item label="资源包下载地址" extra="先上传资源包，再填写可访问的 HTTPS .wgt 链接；版本必须与包内版本一致。">
                <Input aria-label="WGT 下载地址" value={downloadLink} placeholder="https://cdn.example.com/version/app_v3.6.0.wgt" onChange={e => this.setState({ downloadLink: e.target.value })} />
              </Form.Item>
              <Form.Item label="更新说明" extra={`${description.length}/5000，可留空，支持换行。用于 updates 中的 description 字段。`}>
                <Input.TextArea aria-label="WGT 更新说明" value={description} rows={4} maxLength={5000} placeholder="例如：修复上传失败后无法重试、搜索特殊字符等问题" onChange={e => this.setState({ description: e.target.value })} />
              </Form.Item>
              <p>只修改首页版本时，可以保留原 WGT 配置。请勿仅提高 WGT 版本号却继续指向旧包。</p>
            </Card>
            <Button type="primary" htmlType="submit" loading={saving} disabled={disabled || !current}>保存配置</Button>
          </fieldset>
        </Form>
      </Spin>
    </div>;
  }
}

const ConnectedForm = withApollo(ReleaseSettingsForm);
export default function ReleaseSettings() {
  return <Query query={GET_CURRENT_USER}>{({ loading, data }) => {
    if (loading) return <Spin />;
    if (!data || !data.currentUser || data.currentUser.role !== 'admin') return <Alert type="error" message="仅管理员可管理版本配置" />;
    return <ConnectedForm />;
  }}</Query>;
}
